import React, { useEffect } from 'react';
import { useAppConfigContext } from '@/context/AppConfig';
import { useAppConstantsContext } from '@/context/AppConstants';
import { reportGetters } from '@/reports/reportGetters/reportGetters';
import {
  LizanoListInfo,
  LizanoListProperty,
} from '@/reports/sections/ListsSection/lists/lists';
import { useReportRedirect } from '@/reports/useReportRedirect/useReportRedirect';
import {
  SerializedListing,
  SerializedPreforeclosure,
} from '@/search/useSearchListings/Interfaces';
import { Card, Stack, Text, Box, styled } from '@/theme/index';
import { commaFormatCurrency } from '@/utils/commaFormat/commaFormatCurrency';
import { ListPropertyCta } from '../ListPropertyCta/ListPropertyCta';

const PRE_FORECLOSURES_PARAM = 'pre_foreclosures';
export interface ListingDataCardProps {
  listingType: string;
  resultData: SerializedListing | SerializedPreforeclosure;
  lists: LizanoListInfo[];
  enableSaveToListV2?: boolean;
}

export const CardContainer = styled(Card)(({ theme }) => ({
  position: 'relative',
  width: '100%',
  minHeight: '190px',
  borderRadius: '5px',
  cursor: 'pointer',
  padding: '0',
  maxHeight: '285px',
  border: `1px solid ${theme.palette.divider}`,
  boxShadow: 'none',
  '&:hover': {
    boxShadow: '0 2px 6px 1px rgba(117,117,117,0.5)',
  },
}));

const ImageWrapper = styled(Box)(() => ({
  position: 'relative',
  height: '172px',
}));

const ImageToShow = styled('img')(() => ({
  position: 'absolute',
  top: '0',
  right: '0',
  left: '0',
  bottom: '0',
  width: '100%',
  height: 'inherit',
  objectFit: 'cover',
  background: 'none',
}));

const PreforeclosureIcon = styled('div')(({ theme }) => ({
  width: '8px',
  height: '8px',
  display: 'inline-block',
  marginRight: theme.spacing(0.5),
  borderRadius: '100%',
  background: theme.palette.primary.main,
}));

export const TextContainer = styled(Text)(({ theme }) => ({
  background: theme.palette.background.default,
  width: '100%',
  padding: theme.spacing(0.5),
  marginBottom: theme.spacing(0.5),
}));

export const propertyOnList = (
  list: LizanoListInfo,
  street: string | undefined,
  zip: string | undefined | null
) => {
  if (list?.list_properties && street && zip) {
    return list.list_properties.filter((property) => {
      return property.street === street && property.zip5 === zip;
    });
  }
  return [];
};

export const getListPropertyIDs = (listProperties: LizanoListProperty[]) => {
  return listProperties.map((property: LizanoListProperty) =>
    property.id.toString()
  );
};

export function ListingDataCard({
  listingType,
  resultData,
  lists,
  enableSaveToListV2,
}: ListingDataCardProps) {
  const {
    images: { imgNoProperty },
  } = useAppConstantsContext();
  const { redirectToReportUrl } = useReportRedirect();
  const { trackListHub } = useAppConfigContext();

  let displayImg = imgNoProperty;
  if (listingType !== PRE_FORECLOSURES_PARAM) {
    const sources = (resultData as SerializedListing)?.property?.listing_detail
      ?.media?.sources;
    const firstImage = sources?.[0]?.url;
    const secondImage = sources?.slice(-1)?.[0]?.url;
    displayImg = firstImage || secondImage || imgNoProperty;
  }
  const { street, zip5, delivery_address_line, city, state, cityState } =
    resultData?.address?.parts || {};
  const address = `${street || delivery_address_line}, ${cityState} ${zip5}`;

  const savedReportList = lists?.filter(
    (list) => list.name === 'saved reports'
  )[0];
  const savedReportListCode = savedReportList?.list_code;
  const listProperty = propertyOnList(
    savedReportList,
    street || delivery_address_line || '',
    zip5
  );
  const listPropertyIDs = getListPropertyIDs(listProperty);
  const reportIsSaved = Boolean(listProperty.length);

  useEffect(() => {
    if (listingType !== PRE_FORECLOSURES_PARAM) {
      // Track event only ONCE per card in ListHub and if type is listing or rental
      const listingKey = (resultData as SerializedListing)?.property?.listing_detail?.key;
      if (listingKey) {
        trackListHub('SEARCH_DISPLAY', listingKey);
      }
    }
  }, []);

  const handleRedirectToReport = async () => {
    if (listingType === PRE_FORECLOSURES_PARAM) {
      const response = await reportGetters.property({
        queryParams: new URLSearchParams({
          address: street || delivery_address_line || '',
          city: city || '',
          state: state || '',
          zipcode: zip5 || '',
        }),
      });

      redirectToReportUrl('property', response.report.permalink);
    }
  };

  return (
    <CardContainer onClick={handleRedirectToReport}>
      {enableSaveToListV2 && (
        <Box sx={{ position: 'absolute', top: '0', right: '-11px' }}>
          {(delivery_address_line || street) && zip5 && (
            <ListPropertyCta
              savedReportListCode={savedReportListCode}
              savedReportOnList={reportIsSaved}
              address={street || delivery_address_line || ''}
              city={city || ''}
              state={state || ''}
              zipcode={zip5 || ''}
              listPropertyIDs={listPropertyIDs}
            />
          )}
        </Box>
      )}
      {listingType !== PRE_FORECLOSURES_PARAM && (
        <ImageWrapper>
          <ImageToShow src={displayImg} alt="Success" />
        </ImageWrapper>
      )}
      <Stack sx={{ padding: '24px 14px' }}>
        {listingType === PRE_FORECLOSURES_PARAM && (
          <>
            <Text variant="h3" fontSize="18px" sx={{ marginBottom: '18px' }}>
              {address}
            </Text>
            <Stack
              direction="row"
              alignItems="center"
              sx={{ marginBottom: '8px' }}
            >
              <PreforeclosureIcon sx={{ marginRight: '4px' }} />
              <Text fontSize={12}>Pre-foreclosure</Text>
            </Stack>
          </>
        )}
        {listingType !== PRE_FORECLOSURES_PARAM && (
          <>
            <Stack direction="row" spacing={1}>
              <Text variant="h3" marginBottom={0}>
                {commaFormatCurrency(
                  (resultData as SerializedListing)?.price?.current,
                  true
                ) || '-'}
              </Text>
              <Text variant="body2">{resultData?.isRental ? ' /mo' : ''}</Text>
            </Stack>
            <Box sx={{ padding: 0 }}>
              <Text component="span" variant="body2" marginRight="12px">
                <Text fontWeight="bold" component="span">
                  {`${
                    (resultData as SerializedListing)?.building?.bedrooms || '-'
                  } `}
                </Text>
                Beds
              </Text>
              <Text component="span" variant="body2" marginRight="12px">
                <Text fontWeight="bold" component="span">
                  {`${
                    (resultData as SerializedListing)?.building?.bathrooms ||
                    '-'
                  } `}
                </Text>
                Baths
              </Text>
              <Text component="span" variant="body2">
                <Text fontWeight="bold" component="span">
                  {`${
                    (resultData as SerializedListing)?.building?.size_home ||
                    '-'
                  } `}
                </Text>
                Sq Ft
              </Text>
            </Box>
            <Text fontSize={12}>{address}</Text>
          </>
        )}
        {(resultData as SerializedPreforeclosure)?.events?.[0]
          ?.recording_date && (
          <TextContainer fontSize={12}>
            Recorded on
            <Text component="span" fontSize={12} fontWeight="bold">
              {` ${
                (resultData as SerializedPreforeclosure)?.events?.[0]
                  ?.recording_date?.full
              }`}
            </Text>
          </TextContainer>
        )}
        {(resultData as SerializedPreforeclosure)?.events?.[0]?.type && (
          <TextContainer fontSize={12}>
            Type:
            <Text component="span" fontWeight="bold" fontSize={12}>
              {` ${(resultData as SerializedPreforeclosure)?.events[0].type}`}
            </Text>
          </TextContainer>
        )}
      </Stack>
    </CardContainer>
  );
}
